import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, createFilterableDateRangeColumnDefinition, createFilterableNumberRangeColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IHealthRiskAssessment, { IHealthRiskAssessmentCreateOrUpdate, IHealthRiskAssessmentFilters, IHealthRiskAssessmentInitResult } from "models/sql/IHealthRiskAssessment";
import { useContext, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import HealthRiskAssessmentService from "services/HealthRiskAssessmentService";
import { formatDate } from "utilities/StringUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { addNewLogEntry, getBaseDomain } from "utilities/MiscUtilities";
import MiscService from "services/MiscService";
import React from "react";
import CommonLookupDisplay from "components/CommonLookupDisplay";
import { applyGlobalFilterData } from "utilities/MiscUtilities";

interface IHealthRiskAssessmentsLookupProps {
    baseData?: IHealthRiskAssessmentInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const HealthRiskAssessmentsLookup = (props: IHealthRiskAssessmentsLookupProps) => {
    const [filter, setFilter] = useState<IHealthRiskAssessmentFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [forceRefresh, setForceRefresh] = useState(new Date());

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        HealthRiskAssessmentService.DownloadExcelFileAsync(
            accessToken,
            "HealthRiskAssessmentExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const saveEntryUpdate = async (healthRiskAssessment: IHealthRiskAssessment, allData: IHealthRiskAssessment[]) => {
        globalContext.setLoading(true);
        const accessToken = await globalContext.getAccessTokenAsync();

        const data: IHealthRiskAssessmentCreateOrUpdate = {
            healthRiskAssessmentId: healthRiskAssessment.healthRiskAssessmentId,
            stationId: healthRiskAssessment.stationId,
            fireDepartmentId: healthRiskAssessment.fireDepartmentId,
            answersJson: healthRiskAssessment.answersJson,
            stationLocationFactorScore: healthRiskAssessment.stationLocationFactorScore,
            stationPpegearScore: healthRiskAssessment.stationPpegearScore,
            stationCommonAreasScore: healthRiskAssessment.stationCommonAreasScore,
            apparatusVehicleFactorsScore: healthRiskAssessment.apparatusVehicleFactorsScore,
            apparatusVehicleDeconScore: healthRiskAssessment.apparatusVehicleDeconScore,
            apparatusPpegearScore: healthRiskAssessment.apparatusPpegearScore,
            incidentEquipmentHandlingScore: healthRiskAssessment.incidentEquipmentHandlingScore,
            incidentProtectiveEquipmentScore: healthRiskAssessment.incidentProtectiveEquipmentScore,
            incidentGrossDeconScore: healthRiskAssessment.incidentGrossDeconScore,
            teamRiskAwarenessScore: healthRiskAssessment.teamRiskAwarenessScore,
            teamPovuseScore: healthRiskAssessment.teamPovuseScore,
            teamHealthWellnessScore: healthRiskAssessment.teamHealthWellnessScore,
            zipCode: healthRiskAssessment.zipCode,
            departmentName: healthRiskAssessment.departmentName,
            stationNumber: healthRiskAssessment.stationNumber,
            submissionDate: healthRiskAssessment.submissionDate,
            region: healthRiskAssessment.region,
            country: healthRiskAssessment.country,
            city: healthRiskAssessment.city,
            notes: healthRiskAssessment.notes,
            yearBuilt: healthRiskAssessment.yearBuilt,
            systemDataJson: healthRiskAssessment.systemDataJson,
            userEmail: healthRiskAssessment.userEmail,
            userName: healthRiskAssessment.userName,
            salesRepId: healthRiskAssessment.salesRepId,
            code: healthRiskAssessment.code,
            source: healthRiskAssessment.source,
            dealerId: healthRiskAssessment.dealerId,
            shift: healthRiskAssessment.shift
        };

        await HealthRiskAssessmentService.CreateOrUpdateAsync(accessToken, data);
        allData[allData.findIndex(x => x.healthRiskAssessmentId === healthRiskAssessment.healthRiskAssessmentId)] =
        {
            ...data,
            fireDepartment: healthRiskAssessment.fireDepartment,
            salesRep: healthRiskAssessment.salesRep
        };

        //setForceRefresh(new Date());
        globalContext.setLoading(false);
    };

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    const potentialScore = (hra: IHealthRiskAssessment) => {
        if (!hra.fireDepartment || !hra.fireDepartment.numberOfStations) {
            return "N/A";
        }

        var totalScore = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        // @ts-ignore
        return (((108 - totalScore) * hra.fireDepartment.numberOfStations) / 10)
    };

    const getColumns = (): IColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>[] => {
        return [
            {
                propName: "healthRiskAssessmentId",
                title: "Follow Up",
                customRenderer: (value, row, allData) => <td className="align-top" key={`followup${row.healthRiskAssessmentId}`}>
                    {
                        (row.systemDataJson ?? "").indexOf("follow up email") > -1 ?
                            <span className="ps-2"><FontAwesomeIcon icon={faCheck as IconProp} /></span> :
                            <span
                                title="Send Follow Up Email"
                                className="quick-link"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    globalContext.showModal({
                                        content:
                                            <></>,
                                        title: "Send Follow Up Email",
                                        formElements: [
                                            {
                                                key: "title",
                                                type: "text",
                                                title: `Email Title`,
                                                value: "Regarding your Health Risk Assessment"
                                            },
                                            {
                                                key: "body",
                                                type: "textarea",
                                                title: `Body`,
                                                style: { minHeight: "200px" },
                                                value: `We recently received your Health Risk Assessment and would like to discuss your results. <a href=\"${window.location.protocol}//${getBaseDomain()}/Public/HRAReport?code=${row.code}\">View your results</a>`
                                            }
                                        ],
                                        okCallback:
                                            async (data) => {
                                                var emailAddress = row.userEmail;
                                                var fromEmailAddress = globalContext.userEmail;
                                                var title = data?.[0].value ?? "";
                                                var body = data?.[1].value ?? "";

                                                if (emailAddress) {
                                                    const accessToken = await globalContext.getAccessTokenAsync();

                                                    await MiscService.sendEmailRawAsync(
                                                        accessToken,
                                                        emailAddress,
                                                        fromEmailAddress,
                                                        title,
                                                        body);

                                                    window.alert("Your follow up email has been sent to " + emailAddress);

                                                    await saveEntryUpdate(
                                                        {
                                                            ...addNewLogEntry(
                                                                (new Date()),
                                                                `${globalContext.userName} has sent a follow up email to '${row.userEmail}'`,
                                                                globalContext.userEmail,
                                                                101,
                                                                row)
                                                        },
                                                        allData);
                                                }
                                            },
                                        cancelCallback: () => { },
                                        customActionButton: {
                                            title: "Manually Contacted",
                                            buttonType: "success",
                                            onClick: () => {
                                                saveEntryUpdate(
                                                    {
                                                        ...addNewLogEntry(
                                                            (new Date()),
                                                            `${globalContext.userName} manually sent a follow up email to '${row.userEmail}'`,
                                                            globalContext.userEmail,
                                                            101,
                                                            row)
                                                    },
                                                    allData);
                                            },
                                            closesModal: true
                                        }
                                    });
                                }}>
                                <FontAwesomeIcon icon={faEnvelope as IconProp} />
                            </span>
                    }
                </td>
            },
            createFilterableDateRangeColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                "submissionDate",
                // @ts-ignore
                "createdDate",
                true,
                filter,
                (value, row) => <td className="align-top" key={`submissionDate${row.healthRiskAssessmentId}`}>
                    {formatDate(value)}</td>,
                "Created Date"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                // @ts-ignore
                "repCompanies",
                "repCompanies",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`repCompanies${row.healthRiskAssessmentId}`}>
                    {row.salesRep?.dealer?.dealerName ?? row.dealer?.dealerName ?? row.salesRep?.company ?? ""}</td>,
                "Rep Company"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                // @ts-ignore
                "departmentNames",
                "departmentNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`departmentNames${row.healthRiskAssessmentId}`}>
                    {row.fireDepartment?.fireDeptName ?? row.departmentName ?? ""}</td>,
                "Department"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                "userName",
                "names",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`userName${row.healthRiskAssessmentId}`}>
                    {
                        (row.userName || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Name"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                "userEmail",
                "emails",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`userEmail${row.healthRiskAssessmentId}`}>
                    {
                        (row.userEmail || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Email"),
            createFilterableNumberRangeColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                // @ts-ignore
                "score",
                // @ts-ignore
                "score",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`score${row.healthRiskAssessmentId}`}>{calcScore(row)}</td>,
                "Score"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                "city",
                "cities",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`city${row.healthRiskAssessmentId}`}>
                    {row.city ?? ""}</td>,
                "City"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                // @ts-ignore
                "states",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.healthRiskAssessmentId}`}>
                    {row.fireDepartment?.hqstate ?? ""}</td>,
                "State"),
            createFilterableArrayColumnDefinition<IHealthRiskAssessment, IHealthRiskAssessmentFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`country${row.healthRiskAssessmentId}`}>
                    {
                        (row.country || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Country")
        ];
    };

    const rowClassRenderer = (row: IHealthRiskAssessment) =>
        (row.systemDataJson ?? "").indexOf("follow up email") > -1 ? "followedUp" : "notFollowedUp";

    const getTools = () => [
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>
    ];

    return (
        <CommonLookupDisplay<IHealthRiskAssessmentFilters, IHealthRiskAssessment, IHealthRiskAssessmentCreateOrUpdate>
            baseFilters={{
                sortBys: ["healthRiskAssessmentId"],
                sortDescending: true
            }}
            filterHints={{
                regions: "Array",
                names: "Array",
                emails: "Array",
                sortBys: "Array",
                countries: "Array",
                states: "Array",
                sortDescending: "Boolean",
                organizationTypes: "Array"
            }}
            dataTypeName="Heath Risk Assessment"
            dataTypeNamePlural="Heath Risk Assessments"
            pathName="HealthRiskAssessments"
            primaryIdKey="healthRiskAssessmentId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={HealthRiskAssessmentService}
            tools={getTools()}
            rowClassRenderer={rowClassRenderer}
            forceRefresh={forceRefresh}
        />
    );
}

export default HealthRiskAssessmentsLookup;
